import React from 'react'
import styled, { css, keyframes } from 'styled-components'

/*
 * @deprecated Use components/lib/DesignSystem/V3/Skeleton instead.
 */

/**
 * Shimmer animation for skeleton loading effect.
 * @type {import('styled-components').Keyframes}
 */
const shimmer = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 100% 0; }
`

/**
 * Base skeleton styles with shimmer effect.
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    var(--skeleton-shimmer-edge) 25%,
    var(--skeleton-shimmer-mid) 37%,
    var(--skeleton-shimmer-edge) 63%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 3s linear infinite alternate;
  animation-delay: -1.5s;
  border-radius: ${({ borderRadius }) => borderRadius || '10px'};
  max-width: 100%;
  background-clip: padding-box;
  will-change: background-position;
  overflow: hidden;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '20px'};
  ${({ customStyles }) => customStyles}
`

/**
 * Skeleton for title (small line).
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonTitle = styled(SkeletonBase)`
  height: 12px;
  border-radius: 6px;
  ${({ width }) => width && `width: ${width};`}
`

/**
 * Skeleton for text (medium line).
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonText = styled(SkeletonBase)`
  height: 18px;
  border-radius: 6px;
  ${({ width }) => width && `width: ${width};`}
`

/**
 * Skeleton for numbers/values (larger block).
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonNumber = styled(SkeletonBase)`
  height: 33px;
  border-radius: 6px;
  ${({ width }) => width && `width: ${width};`}
`

/**
 * Skeleton for charts (full block).
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonChart = styled(SkeletonBase)`
  border-radius: 10px;
  width: 100%;
  height: 100%;
`

/**
 * Skeleton for circles (icons/avatars).
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonCircle = styled(SkeletonBase)`
  border-radius: 50%;
  ${({ size }) =>
    size &&
    css`
      width: ${size};
      height: ${size};
    `}
`

/**
 * Container for skeleton items with gap.
 * @type {import('styled-components').StyledComponent<'div', any, {}, never>}
 */
export const SkeletonContainer = styled.div`
  display: ${({ display }) => display || 'flex'};
  flex-direction: ${({ direction }) => direction || 'column'};
  gap: ${({ gap }) => gap || '8px'};
  ${({ customStyles }) => customStyles}
`

const variants = {
  text: SkeletonText,
  title: SkeletonTitle,
  number: SkeletonNumber,
  chart: SkeletonChart,
  circle: SkeletonCircle,
  custom: SkeletonBase,
}

/**
 * Main Skeleton component with flexible options.
 *
 * @deprecated Use components/lib/DesignSystem/V3/Skeleton instead.
 *
 * @param {Object} props
 * @param {'text'|'title'|'number'|'chart'|'circle'|'custom'} [props.variant='text'] - Type of skeleton to render.
 * @param {string} [props.width] - Width of the skeleton (CSS value).
 * @param {string} [props.height] - Height of the skeleton (CSS value).
 * @param {string} [props.borderRadius] - Border radius (CSS value).
 * @param {any} [props.customStyles] - Additional CSS styles.
 * @param {string} [props.className] - CSS class name.
 * @returns {JSX.Element}
 */
const Skeleton = ({
  variant = 'text',
  width,
  height,
  borderRadius,
  customStyles,
  className,
  ...props
}) => {
  const Component = variants[variant] || SkeletonBase

  return (
    <Component
      width={width}
      height={height}
      borderRadius={borderRadius}
      customStyles={customStyles}
      className={className}
      {...props}
    />
  )
}

export default Skeleton
export { SkeletonBase, shimmer }
